/**
 * GM Workwear Custom Order Builder — Pricing Engine & Cart Integration
 * ------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Unified Debug Logger — single toggle for all diagnostic output
     Set GM_DEBUG.enabled = false for production
     ------------------------------------------------------------------ */
  const GM_DEBUG = {
    enabled: true,
    log(...args)  { if (this.enabled) console.log('[GM-COB]', ...args); },
    warn(...args) { if (this.enabled) console.warn('[GM-COB]', ...args); },
    error(...args) { if (this.enabled) console.error('[GM-COB]', ...args); },
  };

  const config = window.GM_CUSTOMIZATION || {};
  if (!config.productId) {
    GM_DEBUG.warn('Aborting: window.GM_CUSTOMIZATION.productId is missing. Config:', config);
    return;
  }
  GM_DEBUG.log('Script started. Product ID:', config.productId, 'Title:', config.productTitle);

  const S = config.strings || {};
  const WORKER_URL = config.uploadWorkerUrl || 'https://upload-to-shopify-worker-gmworkwear.noumanakbardeveloper.workers.dev';

  /* ------------------------------------------------------------------
     Mobile Debug Overlay
     ------------------------------------------------------------------ */
  const DEBUG = {
    enabled: window.location.search.includes('debug=1'),
    logs: [],
    maxLogs: 20,
    
    log(msg) {
      if (!this.enabled) return;
      const timestamp = new Date().toLocaleTimeString();
      this.logs.unshift(`[${timestamp}] ${msg}`);
      if (this.logs.length > this.maxLogs) this.logs.pop();
      this.render();
    },
    
    render() {
      if (!this.enabled) return;
      let overlay = document.getElementById('gm-debug-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'gm-debug-overlay';
        overlay.style.cssText = `
          position: fixed;
          bottom: 80px;
          right: 10px;
          width: 280px;
          max-height: 300px;
          background: rgba(0, 0, 0, 0.9);
          color: #0f0;
          font-family: monospace;
          font-size: 10px;
          padding: 8px;
          border-radius: 4px;
          overflow-y: auto;
          z-index: 9999;
          border: 1px solid #0f0;
        `;
        document.body.appendChild(overlay);
      }
      overlay.innerHTML = this.logs.map(l => `<div>${l}</div>`).join('');
    }
  };

  /* ------------------------------------------------------------------
     Variant Lookup — All needed variants loaded in Liquid by iterating
     types and positions, avoiding the 250 variant limit
     ------------------------------------------------------------------ */
  /* Variant lookup debug logging disabled for production */


  /* ------------------------------------------------------------------
     State
     ------------------------------------------------------------------ */
  const state = {
    groups: [],           // finalized color groups
    current: null,        // group being built
    editingIndex: -1,     // index in groups being edited, or -1 for new
    files: new Map(),     // positionKey -> base64 data URL string (uploaded on Add to Cart)
  };

  /* ------------------------------------------------------------------
     DOM refs (lazy)
     ------------------------------------------------------------------ */
  let els = null;
  function getEls() {
    if (els && els.root && !document.contains(els.root)) {
      GM_DEBUG.warn('getEls: cached root no longer in DOM, re-querying');
      els = null;
    }
    if (els) return els;
    const root = document.querySelector('[data-gm-cob]');
    if (!root) {
      GM_DEBUG.warn('getEls: [data-gm-cob] root element not found in DOM');
      return null;
    }
    GM_DEBUG.log('getEls: root element found, querying child elements');
    els = {
      root,
      selectedColors: root.querySelector('.gm-cob__selected-colors'),
      colorBtns: root.querySelectorAll('.gm-cob__color-swatch'),
      sizeInputs: root.querySelectorAll('.gm-cob__qty-input'),
      qtyDecreaseBtns: root.querySelectorAll('.gm-cob__qty-btn--decrease'),
      qtyIncreaseBtns: root.querySelectorAll('.gm-cob__qty-btn--increase'),
      matrixHeader: root.querySelector('.gm-cob__matrix-header'),
      sizeGuideModal: root.querySelector('[data-gm-size-guide-modal]'),
      sizeGuideOpen: root.querySelector('[data-gm-size-guide-open]'),
      sizeGuideClose: root.querySelectorAll('[data-gm-size-guide-close]'),
      addColorBtn: root.querySelector('.gm-cob__add-color-btn'),
      typeBtns: root.querySelectorAll('.gm-cob__type-btn'),
      positionsWrap: root.querySelector('.gm-cob__positions-wrap'),
      positionCards: () => root.querySelectorAll('.gm-cob__position-card'),
      positionsSummary: root.querySelector('.gm-cob__positions-summary'),
      uploadsWrap: root.querySelector('.gm-cob__uploads-wrap'),
      uploadZones: () => root.querySelectorAll('.gm-cob__upload-zone'),
      breaksRows: root.querySelectorAll('.gm-cob__breaks-row'),
      breakCards: () => root.querySelectorAll('.gm-cob__breaks-card'),
      breaksHint: root.querySelector('[data-gm-breaks-hint]'),
      deliveryMessage: document.querySelector('[data-gm-delivery-message]'),
      cartBtn: root.querySelector('.gm-cob__cart-btn'),
      errors: root.querySelector('.gm-cob__errors'),
      summaryImage: root.querySelector('[data-gm-summary-image]'),
      summaryTitle: root.querySelector('[data-gm-summary-title]'),
      summaryHint: root.querySelector('[data-gm-summary-hint]'),
      summaryPriceWrap: root.querySelector('[data-gm-summary-price-wrap]'),
      summaryPrice: root.querySelector('[data-gm-summary-price]'),
      summaryVat: root.querySelector('[data-gm-summary-vat]'),
      vatText: root.querySelector('[data-gm-vat-text]'),
      overlay: root.querySelector('[data-gm-overlay]'),
      overlayProcessing: root.querySelector('[data-gm-overlay-processing]'),
      overlayResult: root.querySelector('[data-gm-overlay-result]'),
      overlayStep: root.querySelector('[data-gm-overlay-step]'),
      overlayStatus: root.querySelector('[data-gm-overlay-status]'),
      overlayBar: root.querySelector('[data-gm-overlay-bar]'),
      overlayDetail: root.querySelector('[data-gm-overlay-detail]'),
      overlaySuccessIcon: root.querySelector('[data-gm-overlay-success-icon]'),
      overlayErrorIcon: root.querySelector('[data-gm-overlay-error-icon]'),
      overlayTitle: root.querySelector('[data-gm-overlay-title]'),
      overlayContext: root.querySelector('[data-gm-overlay-context]'),
      overlaySub: root.querySelector('[data-gm-overlay-sub]'),
      overlayActions: root.querySelector('[data-gm-overlay-actions]'),
      cartArea: document.querySelector('.gm-cob__cart-area'),
    };
    GM_DEBUG.log('getEls: elements queried — colorBtns:', els.colorBtns.length, 'sizeInputs:', els.sizeInputs.length, 'typeBtns:', els.typeBtns.length, 'cartBtn:', !!els.cartBtn, 'addColorBtn:', !!els.addColorBtn);
    // Reparent overlay to <body> so it escapes any ancestor transforms
    // (e.g. scroll-trigger animations that create a containing block)
    if (els.overlay && els.overlay.parentElement && els.overlay.parentElement !== document.body) {
      document.body.appendChild(els.overlay);
    }
    // Reparent cart-area to <body> so position: fixed works on all devices
    // (escapes scroll-trigger transforms on parent elements)
    if (els.cartArea && els.cartArea.parentElement && els.cartArea.parentElement !== document.body) {
      document.body.appendChild(els.cartArea);
    }
    return els;
  }

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */
  const fmtMoney = (cents) => {
    const sym = config.currencySymbol || '£';
    return sym + (cents / 100).toFixed(2);
  };

  const findVariant = (colorName, sizeName) => {
    const variants = config.variants || [];
    if (variants.length === 0) {
      GM_DEBUG.warn('findVariant: no variants in config. color:', colorName, 'size:', sizeName);
    }
    // 1. Exact match: variant has both color and size in its options
    let match = variants.find(v => {
      const opts = [v.option1, v.option2, v.option3];
      return opts.includes(colorName) && opts.includes(sizeName);
    });
    if (match) return match;
    // 2. Match by color only
    match = variants.find(v => [v.option1, v.option2, v.option3].includes(colorName));
    if (match) return match;
    // 3. Match by size only
    match = variants.find(v => [v.option1, v.option2, v.option3].includes(sizeName));
    if (match) return match;
    // 4. Fallback to first available variant so the item is never dropped
    GM_DEBUG.warn('findVariant: no match for color:', colorName, 'size:', sizeName, '— falling back to first variant. Total variants:', variants.length);
    return variants[0] || null;
  };

  const normalizePosition = (raw) => {
    if (!raw) return '';
    let s = String(raw).trim().toLowerCase();
    // Fix common misspellings
    s = s.replace(/seleeve/g, 'sleeve');
    // Map short names to full names
    s = s.replace(/^front$/, 'full front');
    s = s.replace(/^back$/, 'full back');
    s = s.replace(/^left leg$/, 'left upper leg');
    s = s.replace(/^right leg$/, 'right upper leg');
    // Normalize punctuation and word order so "Upper Right Leg" matches "Right Upper Leg"
    s = s.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
    return [...new Set(s.split(' '))].sort().join(' ');
  };

  const findCustomizationVariant = (typeName, positionName) => {
    const variants = config.customizationProduct?.variants || [];
    if (!typeName || !positionName) return null;
    if (variants.length === 0) {
      GM_DEBUG.warn('findCustomizationVariant: no customization variants loaded. type:', typeName, 'position:', positionName);
    }

    // Normalize position if it comes as an object from metafields
    const rawPos = typeof positionName === 'object' && positionName !== null ? (positionName.position || positionName.name || '') : positionName;
    const t = String(typeName).trim().toLowerCase();
    const p = normalizePosition(rawPos);

    // --- Pass 1: exact match (option1=type, option2=position) ---
    let match = variants.find(v => {
      const o1 = String(v.option1 || '').trim().toLowerCase();
      const o2 = normalizePosition(v.option2 || '');
      return o1 === t && o2 === p;
    });
    if (match) return match;

    // --- Pass 2: reversed match (option1=position, option2=type) ---
    match = variants.find(v => {
      const o1 = normalizePosition(v.option1 || '');
      const o2 = String(v.option2 || '').trim().toLowerCase();
      return o1 === p && o2 === t;
    });
    if (match) return match;

    // --- Pass 3: title segment match ---
    match = variants.find(v => {
      const title = normalizePosition(v.title || '');
      return title.includes(t) && title.includes(p);
    });
    if (match) return match;

    // --- Pass 4: partial option match (e.g. metafield "Front" → variant "Full Front") ---
    match = variants.find(v => {
      const o1 = String(v.option1 || '').trim().toLowerCase();
      const o2 = normalizePosition(v.option2 || '');
      if (o1 !== t) return false;
      // option2 contains position OR position contains option2
      return o2.includes(p) || p.includes(o2);
    });
    if (match) return match;

    // --- Pass 5: title partial match (type exact, position partial) ---
    match = variants.find(v => {
      const title = normalizePosition(v.title || '');
      const opt2 = normalizePosition(v.option2 || '');
      if (!title.includes(t)) return false;
      // title contains position OR position is contained in option2
      return title.includes(p) || p.includes(opt2) || opt2.includes(p);
    });
    if (match) return match;

    GM_DEBUG.warn('findCustomizationVariant: no match found for type:', typeName, 'position:', positionName, '— variants checked:', variants.length);
    return null;
  };

  const findCombinedCustomizationVariant = (typeName, positionNames) => {
    const variants = config.customizationProduct?.variants || [];
    if (!typeName || !positionNames || positionNames.length <= 1) return null;

    const t = String(typeName).trim().toLowerCase();
    const positions = positionNames
      .map(p => normalizePosition(p.position || p))
      .filter(Boolean)
      .sort();

    if (positions.length === 0) return null;

    // --- Pass 1: option2 contains every position name ---
    let match = variants.find(v => {
      const o1 = String(v.option1 || '').trim().toLowerCase();
      if (o1 !== t) return false;
      const o2 = normalizePosition(v.option2 || '');
      return positions.every(p => o2.includes(p));
    });
    if (match) return match;

    // --- Pass 2: title contains type and every position name ---
    match = variants.find(v => {
      const title = normalizePosition(v.title || '');
      if (!title.includes(t)) return false;
      return positions.every(p => title.includes(p));
    });
    if (match) return match;

    return null;
  };

  /* ------------------------------------------------------------------
     Fetch all customization product variants via paginated product.json
     ------------------------------------------------------------------ */
  async function loadCustomizationVariants() {
    const cp = config.customizationProduct;
    if (!cp?.handle) {
      GM_DEBUG.warn('loadCustomizationVariants: no customization product handle in config');
      return;
    }
    GM_DEBUG.log('loadCustomizationVariants: handle:', cp.handle, 'initialLoaded:', (cp.variants || []).length, 'total:', cp.variantsCount || (cp.variants || []).length);

    const initialLoaded = (cp.variants || []).length;
    const total = cp.variantsCount || initialLoaded;
    if (initialLoaded >= total) {
      cp.hasMoreVariants = false;
      return;
    }

    const byId = new Map();
    (cp.variants || []).forEach(v => { if (v && v.id) byId.set(v.id, v); });

    const pageSize = 250;
    const totalPages = Math.ceil(total / pageSize);
    const startPage = Math.floor(initialLoaded / pageSize) + 1;

    for (let page = startPage; page <= totalPages; page++) {
      try {
        const response = await fetch(`/products/${cp.handle}.json?page=${page}`);
        if (!response.ok) {
          GM_DEBUG.warn('loadCustomizationVariants: fetch page', page, 'returned', response.status);
          break;
        }
        const data = await response.json();
        const pageVariants = data.product?.variants || [];
        let added = 0;
        pageVariants.forEach(v => {
          if (v && v.id && !byId.has(v.id)) {
            byId.set(v.id, v);
            added++;
          }
        });
        /* page loaded */
      } catch (e) {
        GM_DEBUG.error('loadCustomizationVariants: fetch error on page', page, e);
        break;
      }
    }

    cp.variants = Array.from(byId.values());
    cp.hasMoreVariants = false;
    GM_DEBUG.log('loadCustomizationVariants: complete. Total variants loaded:', cp.variants.length);

    if (state.current?.type) {
      renderPositions();
      updatePricingDisplay();
    }
  }

  const getWidgetQty = () => {
    let total = 0;
    state.groups.forEach(g => {
      Object.values(g.sizes || {}).forEach(q => total += (parseInt(q, 10) || 0));
    });
    if (state.current) {
      Object.values(state.current.sizes || {}).forEach(q => total += (parseInt(q, 10) || 0));
    }
    return total;
  };

  const getTotalQty = () => getWidgetQty() + cachedCollectionCartQty;

  const getDiscountTier = (qty) => {
    const tiers = (config.discountTiers || []).slice().sort((a, b) => a.min_quantity - b.min_quantity);
    let matched = null;
    for (const t of tiers) {
      const max = t.max_quantity != null ? t.max_quantity : Infinity;
      if (qty >= t.min_quantity && qty <= max) matched = t;
    }
    return matched;
  };

  const getPositionPrice = (type, position) => {
    if (!type || !position) return 0;
    const variant = findCustomizationVariant(type, position);
    if (!variant) {
      GM_DEBUG.warn('getPositionPrice: no variant found — type:', type, 'position:', position, '→ price: 0');
    }
    return variant ? (variant.price || 0) : 0;
  };

  const getCustomizationPerUnit = () => {
    const type = state.current?.type || '';
    if (!type) return 0;
    const positions = state.current?.positions || [];
    if (positions.length === 0) return 0;
    GM_DEBUG.log('getCustomizationPerUnit: type:', type, 'positions:', positions.length, 'allowMultiple:', config.allowMultiplePositions);

    // In multiple mode with multiple positions, try to find a single combined variant first
    const multiMode = config.allowMultiplePositions === 'multiple';
    if (multiMode && positions.length > 1) {
      const combined = findCombinedCustomizationVariant(type, positions);
      if (combined) return combined.price || 0;
    }

    return positions.reduce((sum, p) => sum + getPositionPrice(type, p.position), 0);
  };

  const groupTotalQty = (group) => {
    return Object.values(group.sizes || {}).reduce((s, q) => s + (parseInt(q, 10) || 0), 0);
  };

  /* ------------------------------------------------------------------
     Pricing
     ------------------------------------------------------------------ */
  function calculateTotals() {
    const collectionQty = getTotalQty();
    const widgetQty = getWidgetQty();
    const tier = getDiscountTier(collectionQty);
    const discountPct = tier ? (tier.discount_percentage || 0) : 0;
    const discountMul = 1 - discountPct / 100;
    const cusPerUnit = getCustomizationPerUnit();

    GM_DEBUG.log('calculateTotals: widgetQty:', widgetQty, 'collectionCartQty:', cachedCollectionCartQty, 'totalCollectionQty:', collectionQty, 'discountPct:', discountPct, 'cusPerUnit:', cusPerUnit, 'tiers:', (config.discountTiers || []).length, 'collectionHandle:', config.collectionHandle);

    let lineBaseTotal = 0;

    // Current group
    if (state.current && state.current.color) {
      for (const [size, qtyStr] of Object.entries(state.current.sizes || {})) {
        const qty = parseInt(qtyStr, 10) || 0;
        if (qty <= 0) continue;
        const variant = findVariant(state.current.color, size);
        const base = variant ? (variant.price || 0) : 0;
        lineBaseTotal += (base * discountMul + cusPerUnit) * qty;
      }
    }

    // Finalized groups
    state.groups.forEach(g => {
      for (const [size, qtyStr] of Object.entries(g.sizes || {})) {
        const qty = parseInt(qtyStr, 10) || 0;
        if (qty <= 0) continue;
        const variant = findVariant(g.color, size);
        const base = variant ? (variant.price || 0) : 0;
        lineBaseTotal += (base * discountMul + cusPerUnit) * qty;
      }
    });

    GM_DEBUG.log('calculateTotals: grandTotal:', lineBaseTotal, 'discountTier:', tier ? tier.min_quantity : 'none');
    return { totalQty: collectionQty, widgetQty, discountPct, tier, grandTotal: lineBaseTotal };
  }

  function updatePricingDisplay() {
    const e = getEls();
    if (!e) return;
    const { totalQty, widgetQty, grandTotal } = calculateTotals();

    const breakCards = Array.from(e.root?.querySelectorAll('.gm-cob__breaks-card') || []);
    GM_DEBUG.log('updatePricingDisplay: breakCards:', breakCards.length, 'totalQty:', totalQty, 'grandTotal:', grandTotal);

    // Determine the active tier based on total quantity
    let activeIndex = 0;
    breakCards.forEach((card, idx) => {
      const min = parseInt(card.dataset.min, 10) || 0;
      if (totalQty >= min) {
        activeIndex = idx;
      }
    });

    // Highlight active card only
    breakCards.forEach((card, idx) => {
      card.classList.toggle('active', idx === activeIndex);
    });

    // Update price display on each tier card (base + customization cost)
    const cusPerUnit = getCustomizationPerUnit();
    breakCards.forEach(card => {
      const priceEl = card.querySelector('.gm-cob__breaks-card-price');
      if (!priceEl) return;
      const basePrice = parseInt(card.dataset.basePrice, 10) || 0;
      const rowPct = parseFloat(card.dataset.discount) || 0;
      const discounted = basePrice * (1 - rowPct / 100) + cusPerUnit;
      updateCardPrice(priceEl, discounted);
    });

    // Update inline "X away" hint below the pricing cards
    const hintEl = e.breaksHint;
    if (hintEl) {
      hintEl.hidden = true;
      hintEl.textContent = '';

      const nextIndex = activeIndex + 1;
      if (nextIndex < breakCards.length) {
        const nextCard = breakCards[nextIndex];
        const nextMin = parseInt(nextCard.dataset.min, 10) || 0;
        const nextDiscount = parseFloat(nextCard.dataset.discount) || 0;
        const away = Math.max(0, nextMin - totalQty);
        // Show hint when customer is getting close to the next tier
        // Thresholds: 3 items towards 5+, 7 towards 10+, 20 towards 25+, 40 towards 50+
        const proximityMap = { 5: 3, 10: 7, 25: 20, 50: 40 };
        const proximityStart = proximityMap[nextMin] !== undefined
          ? proximityMap[nextMin]
          : Math.max(0, nextMin - 3);
        if (away > 0 && totalQty >= proximityStart) {
          hintEl.textContent = (S.discountTooltipTpl || 'Only {away} away from {discount}% discount')
            .replace('{away}', away)
            .replace('{discount}', nextDiscount);
          hintEl.hidden = false;
        }
      }
    }

    updateSummary(widgetQty, grandTotal);
    updateDeliveryMessage(breakCards, activeIndex);
  }

  function updateSummary(totalQty, grandTotal) {
    const e = getEls();
    if (!e) return;

    const colorName = state.current?.color || '';
    const colorInfo = (config.colors || []).find(c => c.name === colorName) || {};
    if (e.summaryImage && colorInfo.image) {
      e.summaryImage.src = colorInfo.image;
      e.summaryImage.alt = colorInfo.name || config.productTitle || '';
    }
    if (e.summaryTitle) {
      const title = config.productTitle || '';
      if (totalQty > 0) {
        const text = (S.summaryTitleTpl || '{qty}nbsp;{color} {product}')
          .replace('{qty}', totalQty)
          .replace('{color}', colorName)
          .replace('{product}', title);
        e.summaryTitle.textContent = text.replace(/\s{2,}/g, ' ').trim();
      } else {
        e.summaryTitle.textContent = title;
      }
    }

    const hasQty = totalQty > 0;
    if (e.summaryHint) e.summaryHint.hidden = hasQty;
    if (e.summaryPriceWrap) e.summaryPriceWrap.hidden = !hasQty;

    const vatCheckbox = e.root?.querySelector('.gm-cob__vat-checkbox');
    const vatRate = vatCheckbox ? parseInt(vatCheckbox.dataset.vatRate, 10) || 20 : 20;
    const isExVat = vatCheckbox?.checked || false;

    if (hasQty) {
      const vatMultiplier = 1 + (vatRate / 100);
      const displayTotal = isExVat ? Math.round(grandTotal / vatMultiplier) : grandTotal;
      if (e.summaryPrice) e.summaryPrice.textContent = fmtMoney(displayTotal);
      if (e.summaryVat) e.summaryVat.textContent = isExVat ? (S.vatLabelEx || 'ex. VAT') : (S.incVatLabel || 'inc. VAT');
    }

    if (e.vatText) e.vatText.textContent = isExVat ? (S.vatLabelEx || 'ex. VAT') : (S.vatLabelInc || 'inc. VAT');

    if (e.cartBtn) e.cartBtn.disabled = !hasQty;
  }

  function pulseElement(el) {
    if (!el) return;
    el.classList.remove('pulse');
    void el.offsetWidth;
    el.classList.add('pulse');
    setTimeout(() => el.classList.remove('pulse'), 420);
  }

  let cachedCartTotalCents = 0;
  let cachedCollectionCartQty = 0;

  async function refreshCartTotal() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      cachedCartTotalCents = (cart.total_price || 0);

      const collectionIds = config.collectionProductIds || [];
      GM_DEBUG.log('refreshCartTotal: collectionHandle:', config.collectionHandle, 'collectionProductIds count:', collectionIds.length, 'cart items:', (cart.items || []).length);
      const matchedItems = [];
      cachedCollectionCartQty = (cart.items || []).reduce((sum, item) => {
        if (!item.product_id) return sum;
        if (collectionIds.includes(item.product_id)) {
          matchedItems.push({ productId: item.product_id, qty: item.quantity, title: item.title });
          return sum + (item.quantity || 0);
        }
        return sum;
      }, 0);
      GM_DEBUG.log('refreshCartTotal: matched collection cart qty:', cachedCollectionCartQty, 'matched items:', matchedItems);
    } catch (err) { GM_DEBUG.error('refreshCartTotal: fetch /cart.js failed', err); }
  }

  function updateDeliveryMessage(breakCards, activeIndex) {
    const e = getEls();
    if (!e.deliveryMessage) return;

    const msgEl = e.deliveryMessage;
    const thresholdEx = parseFloat(msgEl.dataset.threshold || '0') * 100 || 0;
    const thresholdInc = parseFloat(msgEl.dataset.thresholdIncVat || msgEl.dataset.threshold || '0') * 100 || 0;
    const { grandTotal } = calculateTotals();

    // Shipping threshold always uses inc-VAT totals, regardless of VAT toggle
    const widgetTotal = grandTotal;
    const cartTotal = cachedCartTotalCents;
    const combinedTotal = widgetTotal + cartTotal;
    const threshold = thresholdInc;

    const defaultTpl = msgEl.dataset.defaultMessage || 'Spend {amount} more for Free Delivery';
    const qualifiedMsg = msgEl.dataset.qualifiedMessage || 'You Have Qualified for Free Delivery!';
    const textEl = msgEl.querySelector('.gm-cob__delivery-text');

    if (combinedTotal >= threshold) {
      msgEl.classList.remove('approaching');
      msgEl.classList.add('qualified');
      if (textEl) textEl.textContent = qualifiedMsg;
    } else {
      const remaining = threshold - combinedTotal;
      msgEl.classList.remove('qualified');
      msgEl.classList.add('approaching');
      if (textEl) textEl.textContent = defaultTpl.replace('{amount}', fmtMoney(remaining));
    }

  }

  function updateCardPrice(priceEl, baseCents) {
    const e = getEls();
    if (!e) return;
    const vatCheckbox = e.root?.querySelector('.gm-cob__vat-checkbox');
    const vatRate = vatCheckbox ? parseInt(vatCheckbox.dataset.vatRate, 10) || 20 : 20;
    const isExVat = vatCheckbox?.checked || false;
    
    let displayCents = baseCents;
    if (isExVat) {
      const vatMultiplier = 1 + (vatRate / 100);
      displayCents = Math.round(baseCents / vatMultiplier);
    }
    priceEl.textContent = fmtMoney(displayCents);
  }

  /* ------------------------------------------------------------------
     Group Management
     ------------------------------------------------------------------ */
  function resetCurrentGroup() {
    const savedType = state.current?.type || '';
    const savedPositions = state.current?.positions ? [...state.current.positions] : [];
    state.current = {
      color: '',
      sizes: {},
      type: savedType,
      positions: savedPositions,
    };
    state.files.clear();
    state.editingIndex = -1;
  }

  function fullResetCurrentGroup() {
    state.current = {
      color: '',
      sizes: {},
      type: '',
      positions: [],
    };
    state.files.clear();
    state.editingIndex = -1;
  }

  function renderSelectedColors() {
    const e = getEls();
    if (!e || !e.selectedColors) return;

    e.selectedColors.innerHTML = '';
    state.groups.forEach((g, idx) => {
      const colorInfo = (config.colors || []).find(c => c.name === g.color) || { name: g.color };
      const total = groupTotalQty(g);
      const div = document.createElement('div');
      div.className = 'gm-cob__color-card gm-cob__anim-in';
      div.innerHTML = `
        <span class="gm-cob__color-card-swatch" style="background:${colorInfo.hex || '#ccc'};${colorInfo.image ? `background-image:url(${colorInfo.image});background-size:cover;` : ''}"></span>
        <div class="gm-cob__color-card-info">
          <div class="gm-cob__color-card-name">${colorInfo.name}</div>
          <div class="gm-cob__color-card-qty">${total} ${S.piecesSuffix || 'pieces'}</div>
        </div>
        <div class="gm-cob__color-card-actions">
          <button type="button" data-edit="${idx}" title="${S.editTitle || 'Edit'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </button>
          <button type="button" data-remove="${idx}" title="${S.removeTitle || 'Remove'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
      `;
      e.selectedColors.appendChild(div);
    });

    // Bind edit/remove
    e.selectedColors.querySelectorAll('button[data-edit]').forEach(btn => {
      btn.addEventListener('click', () => editGroup(parseInt(btn.dataset.edit, 10)));
    });
    e.selectedColors.querySelectorAll('button[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => removeGroup(parseInt(btn.dataset.remove, 10)));
    });
  }

  function editGroup(idx) {
    const g = state.groups[idx];
    if (!g) return;
    const globalType = state.current?.type || '';
    const globalPositions = state.current?.positions ? [...state.current.positions] : [];
    state.current = JSON.parse(JSON.stringify(g));
    state.current.type = globalType;
    state.current.positions = globalPositions;
    state.files = new Map(Object.entries(g.files || {}));
    state.editingIndex = idx;
    state.groups.splice(idx, 1);
    renderSelectedColors();
    syncUIFromCurrent();
    updateAddColorButton();
    updatePricingDisplay();
  }

  function removeGroup(idx) {
    state.groups.splice(idx, 1);
    renderSelectedColors();
    updatePricingDisplay();
  }

  function syncUIFromCurrent() {
    const e = getEls();
    if (!e) {
      GM_DEBUG.warn('syncUIFromCurrent: getEls returned null');
      return;
    }
    GM_DEBUG.log('syncUIFromCurrent: state.current =', JSON.stringify(state.current));

    // Color
    e.colorBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.color === state.current.color);
    });
    GM_DEBUG.log('syncUIFromCurrent: colorBtns —', Array.from(e.colorBtns).filter(b => b.classList.contains('active')).length, 'active out of', e.colorBtns.length);

    // Selected color label
    const selectedLabel = e.root.querySelector('[data-gm-selected-color]');
    if (selectedLabel) {
      selectedLabel.textContent = state.current.color || (S.colorNoneSelected || 'None selected');
    }

    // Color label in matrix
    const colorInfo = (config.colors || []).find(c => c.name === state.current.color) || {};
    const swatch = e.matrixHeader?.querySelector('.gm-cob__matrix-color-swatch');
    const label = e.matrixHeader?.querySelector('.gm-cob__matrix-color-name');
    if (swatch) {
      swatch.style.background = colorInfo.hex || '#ccc';
      if (colorInfo.image) {
        swatch.style.backgroundImage = `url(${colorInfo.image})`;
        swatch.style.backgroundSize = 'cover';
      } else {
        swatch.style.backgroundImage = '';
      }
    }
    if (label) label.textContent = colorInfo.name || 'Select a color';

    // Sizes
    e.sizeInputs.forEach(inp => {
      const qty = state.current.sizes[inp.dataset.size] || 0;
      inp.value = String(qty);
      const item = inp.closest('.gm-cob__matrix-item');
      if (item) {
        item.classList.toggle('active', qty > 0);
        const decBtn = item.querySelector('.gm-cob__qty-btn--decrease');
        if (decBtn) decBtn.disabled = qty <= 0;
      }
      if (state.current.color) {
        const variant = findVariant(state.current.color, inp.dataset.size);
        if (!variant || variant.available === false) {
          inp.disabled = true;
          if (item) item.classList.add('gm-cob__matrix-item--disabled');
        } else {
          inp.disabled = false;
          if (item) item.classList.remove('gm-cob__matrix-item--disabled');
        }
      }
    });

    // Type
    e.typeBtns.forEach(btn => {
      const isNoneBtn = btn.dataset.type === 'None';
      const isActive = isNoneBtn ? state.current.type === '' : btn.dataset.type === state.current.type;
      btn.classList.toggle('active', isActive);
    });

    // Positions
    renderPositions();

    // Uploads
    renderUploads();
  }

  /* ------------------------------------------------------------------
     Color Selection
     ------------------------------------------------------------------ */
  function selectColor(colorName) {
    GM_DEBUG.log('selectColor called:', colorName);
    if (!state.current) resetCurrentGroup();
    state.current.color = colorName;
    state.current.sizes = {};
    syncUIFromCurrent();
    updateAddColorButton();
    updatePricingDisplay();

    // Switch media gallery to the color's variant image
    const colorInfo = (config.colors || []).find(c => c.name === colorName);
    if (colorInfo && colorInfo.mediaId && config.sectionId) {
      const gallery = document.querySelector(`[id^="MediaGallery-${config.sectionId}"]`);
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        GM_DEBUG.log('selectColor: switching gallery to mediaId:', colorInfo.mediaId);
        gallery.setActiveMedia(`${config.sectionId}-${colorInfo.mediaId}`);
      } else {
        GM_DEBUG.warn('selectColor: gallery not found or setActiveMedia missing. sectionId:', config.sectionId, 'gallery:', !!gallery);
      }
    }
  }

  /* ------------------------------------------------------------------
     Size Inputs
     ------------------------------------------------------------------ */
  function onSizeChange(size, value) {
    if (!state.current) resetCurrentGroup();
    const qty = parseInt(value, 10) || 0;
    if (qty > 0) {
      state.current.sizes[size] = qty;
    } else {
      delete state.current.sizes[size];
    }
    const e = getEls();
    const input = Array.from(e.sizeInputs).find(i => i.dataset.size === size);
    if (input) {
      input.value = String(qty);
      const item = input.closest('.gm-cob__matrix-item');
      if (item) {
        item.classList.toggle('active', qty > 0);
        const decBtn = item.querySelector('.gm-cob__qty-btn--decrease');
        if (decBtn) decBtn.disabled = qty <= 0;
      }
    }
    updateAddColorButton();
    updatePricingDisplay();
  }

  function changeQuantity(size, delta) {
    const e = getEls();
    const input = Array.from(e.sizeInputs).find(i => i.dataset.size === size);
    if (!input) return;
    const current = parseInt(input.value, 10) || 0;
    const newQty = Math.max(0, current + delta);
    if (newQty === current) return;
    onSizeChange(size, String(newQty));
  }

  function openSizeGuide() {
    const e = getEls();
    if (e.sizeGuideModal) e.sizeGuideModal.hidden = false;
  }

  function closeSizeGuide() {
    const e = getEls();
    if (e.sizeGuideModal) e.sizeGuideModal.hidden = true;
  }

  function updateAddColorButton() {
    const e = getEls();
    if (!e || !e.addColorBtn) return;
    const hasColor = !!state.current?.color;
    const hasQty = groupTotalQty(state.current) > 0;
    e.addColorBtn.classList.toggle('gm-cob__hidden', !(hasColor && hasQty));
  }

  /* ------------------------------------------------------------------
     Add Color Group
     ------------------------------------------------------------------ */
  function addColorGroup() {
    const e = getEls();
    if (!e) return;

    // Validate
    if (!state.current.color) {
      showError(S.errorSelectColor || 'Please select a color first.');
      return;
    }
    const total = groupTotalQty(state.current);
    if (total === 0) {
      showError(S.errorEnterQuantity || 'Please enter a quantity for at least one size.');
      return;
    }

    clearError();
    state.groups.push({ ...state.current, files: Object.fromEntries(state.files) });
    resetCurrentGroup();
    renderSelectedColors();
    syncUIFromCurrent();
    updateAddColorButton();
    updatePricingDisplay();
  }

  /* ------------------------------------------------------------------
     Type Selection
     ------------------------------------------------------------------ */
  function selectType(typeName) {
    GM_DEBUG.log('selectType called:', typeName);
    if (!state.current) resetCurrentGroup();
    if (typeName === 'None') {
      state.current.type = '';
    } else {
      state.current.type = state.current.type === typeName ? '' : typeName;
    }
    state.current.positions = [];
    state.files.clear();

    // Auto-select the first position for the chosen type so its price is included
    if (state.current.type) {
      const typePositions = (config.positionsByType || {})[state.current.type] || [];
      if (typePositions.length > 0) {
        state.current.positions = [{ position: typePositions[0] }];
      }
    }

    syncUIFromCurrent();
    updatePricingDisplay();
  }

  /* ------------------------------------------------------------------
     Positions
     ------------------------------------------------------------------ */
  function renderPositions() {
    const e = getEls();
    if (!e || !e.positionsWrap) {
      GM_DEBUG.warn('renderPositions: getEls null or positionsWrap missing');
      return;
    }

    const step = e.root.querySelector('[data-step="positions"]');
    if (!state.current?.type) {
      e.positionsWrap.classList.add('gm-cob__hidden');
      if (step) step.classList.add('gm-cob__hidden');
      return;
    }
    e.positionsWrap.classList.remove('gm-cob__hidden');
    if (step) step.classList.remove('gm-cob__hidden');

    const wrap = e.positionsWrap.querySelector('.gm-cob__positions');
    if (!wrap) {
      GM_DEBUG.warn('renderPositions: .gm-cob__positions container not found inside positionsWrap');
      return;
    }

    wrap.innerHTML = '';
    
    // Get positions for the selected type
    const positionsByType = config.positionsByType || {};
    const typePositions = positionsByType[state.current.type] || [];
    GM_DEBUG.log('renderPositions: type:', state.current.type, 'positions available:', typePositions.length, 'selected:', state.current.positions.length);
    
    typePositions.forEach(pos => {
      const price = getPositionPrice(state.current.type, pos);
      const vatCheckbox = e.root?.querySelector('.gm-cob__vat-checkbox');
      const vatRate = vatCheckbox ? parseInt(vatCheckbox.dataset.vatRate, 10) || 20 : 20;
      const isExVat = vatCheckbox?.checked || false;
      let displayPrice = price;
      if (isExVat) {
        const vatMultiplier = 1 + (vatRate / 100);
        displayPrice = Math.round(price / vatMultiplier);
      }
      const isActive = state.current.positions.some(p => p.position === pos);
      const card = document.createElement('button');
      card.type = 'button';
      card.className = `gm-cob__position-card ${isActive ? 'active' : ''}`;
      card.dataset.position = pos;
      card.innerHTML = `
        <span class="gm-cob__position-checkbox" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </span>
        <span class="gm-cob__position-content">
          <span class="gm-cob__position-label">${pos}</span>
          <span class="gm-cob__position-price">${fmtMoney(displayPrice)}</span>
        </span>
      `;
      card.addEventListener('click', () => togglePosition(pos));
      wrap.appendChild(card);
    });

    updatePositionsSummary();
  }

  function togglePosition(pos) {
    if (!state.current) return;
    const idx = state.current.positions.findIndex(p => p.position === pos);
    const singleMode = config.allowMultiplePositions === 'single';
    if (idx >= 0) {
      state.current.positions.splice(idx, 1);
      state.files.delete(pos);
    } else {
      if (singleMode) {
        state.current.positions.forEach(p => state.files.delete(p.position));
        state.current.positions = [{ position: pos, artworkUrl: '' }];
      } else {
        state.current.positions.push({ position: pos, artworkUrl: '' });
      }
    }
    renderPositions();
    renderUploads();
    updatePricingDisplay();
  }

  function updatePositionsSummary() {
    const e = getEls();
    if (!e || !e.positionsSummary) return;
    const count = state.current?.positions?.length || 0;
    const total = getCustomizationPerUnit();
    if (count > 0) {
      const tpl = S.positionsSummaryTpl || 'Adds {price} per item for {count} location{s}';
      e.positionsSummary.textContent = tpl
        .replace('{price}', fmtMoney(total))
        .replace('{count}', count)
        .replace('{s}', count > 1 ? 's' : '');
      e.positionsSummary.classList.remove('gm-cob__hidden');
    } else {
      e.positionsSummary.classList.add('gm-cob__hidden');
    }
  }

  /* ------------------------------------------------------------------
     Uploads
     ------------------------------------------------------------------ */
  function renderUploads() {
    const e = getEls();
    if (!e || !e.uploadsWrap) return;

    const step = e.root.querySelector('[data-step="uploads"]');
    const positions = state.current?.positions || [];
    if (!positions.length) {
      e.uploadsWrap.classList.add('gm-cob__hidden');
      if (step) step.classList.add('gm-cob__hidden');
      return;
    }
    e.uploadsWrap.classList.remove('gm-cob__hidden');
    if (step) step.classList.remove('gm-cob__hidden');

    const wrap = e.uploadsWrap.querySelector('.gm-cob__uploads');
    if (!wrap) return;

    // Re-use existing zones, only add missing ones
    const existing = new Map();
    wrap.querySelectorAll('.gm-cob__upload-item').forEach(el => {
      existing.set(el.dataset.position, el);
    });

    positions.forEach(p => {
      if (existing.has(p.position)) return;
      const item = document.createElement('div');
      item.className = 'gm-cob__upload-item gm-cob__anim-in';
      item.dataset.position = p.position;
      item.innerHTML = `
        <label class="gm-cob__upload-label">${(S.uploadLabelTemplate || '{position} Artwork').replace('{position}', p.position)}</label>
        <div class="gm-cob__upload-zone" data-position="${p.position}">
          <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,application/pdf" data-position="${p.position}">
          <div class="gm-cob__upload-text"><span>${S.uploadChooseFile || 'CHOOSE FILE'}</span> ${S.uploadDragDrop || 'or drag & drop'}</div>
          <div class="gm-cob__upload-preview gm-cob__hidden"></div>
        </div>
      `;
      wrap.appendChild(item);
    });

    // Remove zones for deselected positions
    existing.forEach((el, pos) => {
      if (!positions.some(p => p.position === pos)) el.remove();
    });

    bindUploadZones();
  }

  function bindUploadZones() {
    const e = getEls();
    if (!e) return;
    e.uploadZones().forEach(zone => {
      const input = zone.querySelector('input[type="file"]');
      if (input.dataset.bound) return;
      input.dataset.bound = '1';

      input.addEventListener('change', (ev) => {
        const file = ev.target.files[0];
        if (!file) return;
        const pos = zone.dataset.position;

        // Validate file type
        const allowedTypes = [
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/svg+xml',
          'application/pdf',
        ];
        const allowedExts = ['.png', '.jpg', '.jpeg', '.svg', '.pdf'];
        const isValidType = allowedTypes.includes(file.type);
        const isValidExt = allowedExts.some(ext => file.name.toLowerCase().endsWith(ext));
        if (!isValidType && !isValidExt) {
          showError(`Invalid file type. Only PNG, JPG, JPEG, SVG, and PDF are allowed.`);
          input.value = '';
          return;
        }

        // Read file as base64 data URL so it can be uploaded on Add to Cart
        const reader = new FileReader();
        reader.onload = () => { state.files.set(pos, reader.result); };
        reader.readAsDataURL(file);

        // Show preview
        const preview = zone.querySelector('.gm-cob__upload-preview');
        const text = zone.querySelector('.gm-cob__upload-text');
        const removeBtnHtml = `
          <button type="button" class="gm-cob__upload-remove" title="${S.fileRemoveTitle || 'Remove'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        `;
        if (file.type.startsWith('image/')) {
          const url = URL.createObjectURL(file);
          preview.innerHTML = `<img src="${url}" alt="" loading="lazy" decoding="async">${removeBtnHtml}`;
        } else {
          preview.innerHTML = `<span style="font-size:1.2rem;font-weight:600;">${file.name}</span>${removeBtnHtml}`;
        }
        preview.classList.remove('gm-cob__hidden');
        text.classList.add('gm-cob__hidden');

        preview.querySelector('.gm-cob__upload-remove').addEventListener('click', (ev2) => {
          ev2.stopPropagation();
          state.files.delete(pos);
          input.value = '';
          preview.classList.add('gm-cob__hidden');
          text.classList.remove('gm-cob__hidden');
        });
      });

      // Drag & drop
      zone.addEventListener('dragover', (ev) => {
        ev.preventDefault();
        zone.classList.add('drag-over');
      });
      zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
      zone.addEventListener('drop', (ev) => {
        ev.preventDefault();
        zone.classList.remove('drag-over');
        const file = ev.dataTransfer.files[0];
        if (file) {
          const dt = new DataTransfer();
          dt.items.add(file);
          input.files = dt.files;
          input.dispatchEvent(new Event('change'));
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Upload to Shopify Worker
     ------------------------------------------------------------------ */
  async function uploadFile(dataUrl, position, groupId) {
    const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!match) throw new Error('Invalid file data');
    const mimeType = match[1];
    const ext = mimeType.split('/')[1] || 'png';
    const safePos = position.replace(/\s+/g, '-').toLowerCase();
    const ts = Date.now();
    const filename = `artwork-${safePos}-${groupId}-${ts}.${ext}`;

    const res = await fetch(`${WORKER_URL}/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image: dataUrl,
        filename,
        mimeType,
        groupId: groupId || null,
        designId: `${config.productId}-${position}`,
      }),
    });
    const json = await res.json();
    if (!json.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json.fileUrl;
  }

  /* ------------------------------------------------------------------
     Overlay Controller
     ------------------------------------------------------------------ */
  function showOverlay() {
    const e = getEls();
    if (!e || !e.overlay) return;
    e.overlay.classList.add('active');
    e.overlay.setAttribute('aria-hidden', 'false');

    // Reset to processing card
    if (e.overlayProcessing) e.overlayProcessing.classList.add('visible');
    if (e.overlayResult) e.overlayResult.classList.remove('visible');

    // Reset result state
    if (e.overlaySuccessIcon) e.overlaySuccessIcon.classList.remove('visible');
    if (e.overlayErrorIcon) e.overlayErrorIcon.classList.remove('visible');
    if (e.overlayTitle) e.overlayTitle.textContent = '';
    if (e.overlayContext) e.overlayContext.textContent = '';
    if (e.overlaySub) e.overlaySub.textContent = '';
    if (e.overlayActions) e.overlayActions.innerHTML = '';

    // Reset processing state
    if (e.overlayStep) e.overlayStep.textContent = '3';
    if (e.overlayStatus) e.overlayStatus.textContent = S.overlayStepPrepare || 'Preparing your order';
    if (e.overlayBar) e.overlayBar.style.width = '0%';
    if (e.overlayDetail) e.overlayDetail.textContent = '';
  }

  function hideOverlay(delay = 0) {
    const e = getEls();
    if (!e || !e.overlay) return;
    setTimeout(() => {
      e.overlay.classList.remove('active');
      e.overlay.setAttribute('aria-hidden', 'true');
      if (e.overlayProcessing) e.overlayProcessing.classList.remove('visible');
      if (e.overlayResult) e.overlayResult.classList.remove('visible');
    }, delay);
  }

  function setOverlayStep(stepNum, status, detail = '', progressPercent = 0) {
    const e = getEls();
    if (!e || !e.overlayStep) return;
    e.overlayStep.textContent = String(stepNum);
    e.overlayStep.classList.remove('pulse');
    void e.overlayStep.offsetWidth; // reflow
    e.overlayStep.classList.add('pulse');
    if (e.overlayStatus) e.overlayStatus.textContent = status;
    if (e.overlayDetail) e.overlayDetail.textContent = detail;
    if (e.overlayBar) e.overlayBar.style.width = `${progressPercent}%`;
  }

  function switchToResultCard() {
    const e = getEls();
    if (!e) return;
    if (e.overlayProcessing) e.overlayProcessing.classList.remove('visible');
    if (e.overlayResult) e.overlayResult.classList.add('visible');
  }

  function showOverlaySuccess(title, sub, actions = []) {
    const e = getEls();
    if (!e || !e.overlay) return;
    switchToResultCard();
    if (e.overlayErrorIcon) e.overlayErrorIcon.classList.remove('visible');
    if (e.overlaySuccessIcon) e.overlaySuccessIcon.classList.add('visible');
    if (e.overlayTitle) e.overlayTitle.textContent = title;
    if (e.overlayContext) e.overlayContext.textContent = '';
    if (e.overlaySub) e.overlaySub.textContent = sub;
    renderOverlayActions(actions);
  }

  function showOverlayError(title, sub, context = '', actions = []) {
    const e = getEls();
    if (!e || !e.overlay) return;
    switchToResultCard();
    if (e.overlaySuccessIcon) e.overlaySuccessIcon.classList.remove('visible');
    if (e.overlayErrorIcon) e.overlayErrorIcon.classList.add('visible');
    if (e.overlayTitle) e.overlayTitle.textContent = title;
    if (e.overlayContext) e.overlayContext.textContent = context;
    if (e.overlaySub) e.overlaySub.textContent = sub;
    renderOverlayActions(actions);
  }

  function renderOverlayActions(actions) {
    const e = getEls();
    if (!e || !e.overlayActions) return;
    e.overlayActions.innerHTML = '';
    actions.forEach((a, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `gm-cob__overlay-btn gm-cob__overlay-btn--${a.variant || 'primary'}`;
      btn.textContent = a.label;
      if (a.onClick) btn.addEventListener('click', () => a.onClick(btn));
      e.overlayActions.appendChild(btn);
    });
  }

  /* ------------------------------------------------------------------
     Validation
     ------------------------------------------------------------------ */
  function showError(msg) {
    const e = getEls();
    if (!e || !e.errors) return;
    e.errors.textContent = msg;
    e.errors.classList.add('visible');
  }

  function clearError() {
    const e = getEls();
    if (!e || !e.errors) return;
    e.errors.classList.remove('visible');
    e.errors.textContent = '';
  }

  /* ------------------------------------------------------------------
     Cart Submission
     ------------------------------------------------------------------ */
  async function submitToCart() {
    const e = getEls();
    if (!e) return;

    // If current group has data, auto-add it first
    const currentTotal = state.current?.color ? groupTotalQty(state.current) : 0;
    if (currentTotal > 0) {
      const valid = validateCurrent();
      if (!valid) return;
      state.groups.push({ ...state.current, files: Object.fromEntries(state.files) });
      resetCurrentGroup();
      renderSelectedColors();
      syncUIFromCurrent();
    }

    if (state.groups.length === 0) {
      showError(S.errorNoColorGroups || 'Please add at least one color group with quantities.');
      return;
    }

    const totalQty = getTotalQty();
    const tier = getDiscountTier(totalQty);
    const discountPct = tier ? (tier.discount_percentage || 0) : 0;

    const items = [];
    const orderGroupId = `gm-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const globalType = state.current?.type || '';
    const globalPositions = state.current?.positions || [];
    const globalCusPerUnit = getCustomizationPerUnit();

    // Pre-validate all customization variants exist
    const missingVariants = [];
    const multiMode = config.allowMultiplePositions === 'multiple';
    if (globalType && globalPositions.length) {
      if (multiMode && globalPositions.length > 1) {
        const combined = findCombinedCustomizationVariant(globalType, globalPositions);
        if (!combined) {
          missingVariants.push(`${globalType} — ${globalPositions.map(p => p.position).join(' + ')}`);
        }
      } else {
        globalPositions.forEach(p => {
          const cusVariant = findCustomizationVariant(globalType, p.position);
          if (!cusVariant) {
            missingVariants.push(`${globalType} — ${p.position}`);
          }
        });
      }
    }
    if (missingVariants.length > 0) {
      const uniqueMissing = [...new Set(missingVariants)];
      const missingTpl = S.errorMissingCustomization || 'Missing customization: {list}. Please check your product setup.';
      showError(missingTpl.replace('{list}', uniqueMissing.join(', ')));
      return;
    }

    clearError();
    e.cartBtn.disabled = true;
    showOverlay();

    // Step 3: Preparing
    setOverlayStep(3, S.overlayStepPrepare || 'Preparing your order', '', 10);
    await new Promise(r => setTimeout(r, 400));

    // Step 2: Upload artwork files to Shopify via Cloudflare Worker
    const uploadErrors = [];
    const filesToUpload = [];
    for (const g of state.groups) {
      const files = g.files || {};
      for (const [pos, dataUrl] of Object.entries(files)) {
        if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
          filesToUpload.push({ g, pos, dataUrl });
        }
      }
    }

    if (filesToUpload.length > 0) {
      setOverlayStep(2, S.overlayStepUpload || 'Uploading artwork', `${filesToUpload.length} file${filesToUpload.length !== 1 ? 's' : ''}`, 20);
      let uploaded = 0;
      for (const { g, pos, dataUrl } of filesToUpload) {
        try {
          const cdnUrl = await uploadFile(dataUrl, pos, orderGroupId);
          g.files[pos] = cdnUrl;
          uploaded++;
          const pct = 20 + Math.round((uploaded / filesToUpload.length) * 40);
          setOverlayStep(2, S.overlayStepUpload || 'Uploading artwork', `${uploaded} of ${filesToUpload.length}`, pct);
        } catch (err) {
          uploadErrors.push(`${pos}: ${err.message}`);
        }
      }
    }

    if (uploadErrors.length > 0) {
      const uploadErrTpl = S.errorUploadFailed || 'Upload failed: {errors}';
      showOverlayError(
        S.overlayErrorTitle || 'Upload failed',
        uploadErrTpl.replace('{errors}', uploadErrors.join('; ')),
        'Stage: File Upload',
        [
          { label: S.overlayBtnTryAgain || 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          { label: S.overlayBtnClose || 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
        ]
      );
      return;
    }

    // Step 1: Build items and add to cart
    setOverlayStep(1, S.overlayStepCart || 'Adding to cart', '', 65);
    await new Promise(r => setTimeout(r, 300));

    state.groups.forEach(g => {
      for (const [size, qtyStr] of Object.entries(g.sizes || {})) {
        const qty = parseInt(qtyStr, 10) || 0;
        if (qty <= 0) continue;
        const variant = findVariant(g.color, size);
        if (!variant) continue;

        items.push({
          id: variant.id,
          quantity: qty,
        });
      }

      // Add customization product line item(s)
      const groupTotal = groupTotalQty(g);
      if (globalType && globalPositions.length && groupTotal > 0) {
        const groupFiles = g.files || {};
        const fileProps = {};
        for (const [pos, url] of Object.entries(groupFiles)) {
          if (typeof url === 'string' && url.startsWith('http')) {
            fileProps[`gm_artwork_${pos}`] = url;
          }
        }

        if (multiMode && globalPositions.length > 1) {
          const combinedVariant = findCombinedCustomizationVariant(globalType, globalPositions);
          if (combinedVariant) {
            items.push({
              id: combinedVariant.id,
              quantity: groupTotal,
              properties: {
                '_gm_linked_product': config.productId,
                '_gm_color_group_id': orderGroupId,
                ...fileProps,
              },
            });
          } else {
            globalPositions.forEach(p => {
              const cusVariant = findCustomizationVariant(globalType, p.position);
              if (!cusVariant) return;
              items.push({
                id: cusVariant.id,
                quantity: groupTotal,
                properties: {
                  '_gm_linked_product': config.productId,
                  '_gm_color_group_id': orderGroupId,
                  ...fileProps,
                },
              });
            });
          }
        } else {
          globalPositions.forEach(p => {
            const cusVariant = findCustomizationVariant(globalType, p.position);
            if (!cusVariant) return;
            items.push({
              id: cusVariant.id,
              quantity: groupTotal,
              properties: {
                '_gm_linked_product': config.productId,
                '_gm_color_group_id': orderGroupId,
                ...fileProps,
              },
            });
          });
        }
      }
    });

    if (items.length === 0) {
      showOverlayError(
        S.overlayErrorTitle || 'No valid items',
        S.errorNoValidItems || 'No valid items to add. Please check your selections.',
        'Stage: Build Cart Items',
        [
          { label: S.overlayBtnReview || 'Review Selections', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          { label: S.overlayBtnClose || 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
        ]
      );
      return;
    }

    try {
      setOverlayStep(1, S.overlayStepCart || 'Adding to cart', `${items.length} item${items.length !== 1 ? 's' : ''}`, 80);

      const drawer = document.querySelector('cart-drawer');
      const sectionIds = drawer ? drawer.getSectionsToRender().map(s => s.id) : ['cart-drawer', 'cart-icon-bubble'];

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (data.status || data.description) {
        showOverlayError(
          S.overlayErrorTitle || 'Something went wrong',
          data.description || data.message || (S.overlayErrorGeneric || 'Please try again.'),
          `Stage: Add to Cart (HTTP ${res.status})`,
          [
            { label: S.overlayBtnTryAgain || 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
            { label: S.overlayBtnClose || 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          ]
        );
        return;
      }

      if (data.items && data.items.length) {
        // Clear groups after successful add
        state.groups = [];
        fullResetCurrentGroup();
        renderSelectedColors();
        syncUIFromCurrent();
        updateAddColorButton();
        updatePricingDisplay();

        // Trigger theme-wide cart update (subtle refresh in background)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gm-custom-pricing', cartData: data });
        }

        // Success with CTA buttons — user decides what to do next
        const itemCount = data.items.reduce((sum, it) => sum + (it.quantity || 0), 0);
        showOverlaySuccess(
          S.overlaySuccessTitle || 'Added successfully!',
          (S.overlaySuccessSubTpl || '{count} items added to your cart.').replace('{count}', `${itemCount} item${itemCount !== 1 ? 's' : ''}`),
          [
            {
              label: S.overlayBtnViewCart || 'View Cart',
              variant: 'primary',
              onClick: async () => {
                hideOverlay();
                const drawer = document.querySelector('cart-drawer');
                const cartDrawerEl = document.getElementById('CartDrawer');

                if (!drawer || !cartDrawerEl) {
                  window.location.href = '/cart';
                  return;
                }

                // Fetch a fresh cart-drawer section and replace the entire #CartDrawer innerHTML
                try {
                  const sectionRes = await fetch('/cart?section_id=cart-drawer');
                  const sectionHtml = await sectionRes.text();
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(sectionHtml, 'text/html');

                  const newCartDrawer = doc.getElementById('CartDrawer');
                  if (newCartDrawer) {
                    cartDrawerEl.innerHTML = newCartDrawer.innerHTML;
                  }

                  // Sync is-empty class from fetched section
                  const newDrawer = doc.querySelector('cart-drawer');
                  if (newDrawer) {
                    if (newDrawer.classList.contains('is-empty')) {
                      drawer.classList.add('is-empty');
                    } else {
                      drawer.classList.remove('is-empty');
                    }
                  }

                  // Re-bind overlay click listener since inner content was replaced
                  const overlay = drawer.querySelector('#CartDrawer-Overlay');
                  if (overlay) {
                    overlay.addEventListener('click', drawer.close.bind(drawer));
                  }

                  // Update cart icon bubble
                  const newBubble = doc.querySelector('.cart-count-bubble, #cart-icon-bubble .shopify-section');
                  const currentBubble = document.getElementById('cart-icon-bubble');
                  if (newBubble && currentBubble) {
                    const newCount = newBubble.querySelector('.cart-count-bubble');
                    const currentCount = currentBubble.querySelector('.cart-count-bubble');
                    if (newCount && currentCount) {
                      currentCount.innerHTML = newCount.innerHTML;
                    } else {
                      currentBubble.innerHTML = newBubble.innerHTML;
                    }
                  }
                } catch (err) {
                  console.error('gm: failed to refresh cart drawer section', err);
                }

                drawer.open();
              },
            },
            {
              label: S.overlayBtnContinue || 'Continue Shopping',
              variant: 'secondary',
              onClick: () => { hideOverlay(); },
            },
          ]
        );
      } else {
        showOverlayError(
          S.overlayErrorTitle || 'Something went wrong',
          S.overlayErrorEmpty || 'The cart response was empty. Please try again or contact support.',
          'Stage: Cart Response Validation',
          [
            { label: S.overlayBtnTryAgain || 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
            { label: S.overlayBtnClose || 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          ]
        );
      }
    } catch (err) {
      showOverlayError(
        S.overlayErrorTitle || 'Something went wrong',
        S.overlayErrorSub || 'Please try again or contact support.',
        `Stage: Network / ${err.name || 'Error'}`,
        [
          { label: S.overlayBtnTryAgain || 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          { label: S.overlayBtnClose || 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
        ]
      );
    } finally {
      e.cartBtn.disabled = false;
    }
  }

  function validateCurrent() {
    if (!state.current?.color) {
      showError(S.errorSelectColor || 'Please select a color first.');
      return false;
    }
    if (groupTotalQty(state.current) === 0) {
      showError(S.errorEnterQuantity || 'Please enter a quantity for at least one size.');
      return false;
    }
    clearError();
    return true;
  }

  /* ------------------------------------------------------------------
     Event Binding
     ------------------------------------------------------------------ */
  function init() {
    const e = getEls();
    if (!e) {
      GM_DEBUG.error('init: getEls returned null — widget will not initialize. Aborting.');
      return;
    }

    GM_DEBUG.log('init: starting. Colors:', (config.colors || []).length, 'Variants:', (config.variants || []).length, 'DiscountTiers:', (config.discountTiers || []).length, 'TypeBtns:', e.typeBtns.length, 'CustomizationProduct:', !!config.customizationProduct, 'PositionsByType:', Object.keys(config.positionsByType || {}).length);
    DEBUG.log('Init: DOM loaded, setting up event listeners');
    DEBUG.log(`devicePixelRatio: ${window.devicePixelRatio}`);
    DEBUG.log(`innerWidth: ${window.innerWidth}, innerHeight: ${window.innerHeight}`);
    
    // Check if product-info element has transform applied
    const productInfo = document.querySelector('.product__info-container');
    if (productInfo) {
      const pInfoStyle = window.getComputedStyle(productInfo);
      DEBUG.log(`product__info-container transform: ${pInfoStyle.transform}`);
      DEBUG.log(`product__info-container inline style: ${productInfo.getAttribute('style')}`);
    }
    
    // Log summary bar position on init
    if (e.cartArea) {
      const rect = e.cartArea.getBoundingClientRect();
      const computed = window.getComputedStyle(e.cartArea);
      const pos = computed.position;
      const bottom = computed.bottom;
      const zIndex = computed.zIndex;
      DEBUG.log(`Cart area: position=${pos}, bottom=${bottom}, z-index=${zIndex}`);
      DEBUG.log(`Cart area rect: top=${Math.round(rect.top)}px, bottom=${Math.round(rect.bottom)}px, h=${Math.round(rect.height)}px`);
      DEBUG.log(`Viewport: ${window.innerWidth}x${window.innerHeight}px`);
      
      // Check parent elements for position/transform that breaks fixed
      let parent = e.cartArea.parentElement;
      let level = 1;
      while (parent && level <= 8) {
        const pComputed = window.getComputedStyle(parent);
        const pPos = pComputed.position;
        const pTransform = pComputed.transform;
        const pOverflow = pComputed.overflow;
        const hasTransform = pTransform !== 'none';
        
        // Get element identifier
        let tag = parent.tagName.toLowerCase();
        let id = parent.id ? `#${parent.id}` : '';
        let classes = '';
        if (parent.className && typeof parent.className === 'string') {
          const classList = parent.className.split(' ').filter(c => c && c.length > 0);
          classes = classList.length > 0 ? `.${classList.slice(0, 3).join('.')}` : '';
        }
        const identifier = tag + id + classes;
        
        // Always print, highlight transform with asterisk
        const transformMark = hasTransform ? ' ⚠️ TRANSFORM' : '';
        DEBUG.log(`L${level} ${identifier} pos=${pPos} overflow=${pOverflow}${transformMark}`);
        
        // If transform is found, disable it
        if (hasTransform) {
          parent.style.setProperty('transform', 'none', 'important');
          DEBUG.log(`  → Disabled transform on ${identifier}`);
          
          // Recheck to verify it was disabled
          const recheck = window.getComputedStyle(parent).transform;
          DEBUG.log(`  → Recheck: transform is now ${recheck}`);
        }
        
        parent = parent.parentElement;
        level++;
      }
    }

    resetCurrentGroup();
    GM_DEBUG.log('init: resetCurrentGroup done. state.current =', JSON.stringify(state.current));

    // Default: select "None" type
    state.current.type = '';
    syncUIFromCurrent();
    GM_DEBUG.log('init: syncUIFromCurrent done after type reset');

    // Auto-select first color
    const firstColor = config.colors?.[0]?.name;
    if (firstColor) {
      GM_DEBUG.log('init: auto-selecting first color:', firstColor);
      selectColor(firstColor);
    } else {
      GM_DEBUG.warn('init: no colors in config — auto-select skipped. config.colors:', config.colors);
    }

    // Color buttons
    GM_DEBUG.log('init: binding', e.colorBtns.length, 'color button listeners');
    e.colorBtns.forEach(btn => {
      btn.addEventListener('click', () => selectColor(btn.dataset.color));
    });

    // Size inputs
    GM_DEBUG.log('init: binding', e.sizeInputs.length, 'size input listeners');
    e.sizeInputs.forEach(inp => {
      inp.addEventListener('input', () => onSizeChange(inp.dataset.size, inp.value));
      inp.addEventListener('change', () => onSizeChange(inp.dataset.size, inp.value));
    });

    // Quantity +/- buttons and discount tooltip close (delegated)
    e.root.addEventListener('click', (event) => {
      const closeTip = event.target.closest('.gm-cob__breaks-tooltip-close');
      if (closeTip) {
        const tip = closeTip.closest('.gm-cob__breaks-card-tooltip');
        if (tip) tip.hidden = true;
        return;
      }
      const dec = event.target.closest('.gm-cob__qty-btn--decrease');
      const inc = event.target.closest('.gm-cob__qty-btn--increase');
      const btn = dec || inc;
      if (!btn) return;
      const size = btn.dataset.size;
      if (!size) return;
      changeQuantity(size, inc ? 1 : -1);
    });

    // Size guide modal
    if (e.sizeGuideOpen) {
      e.sizeGuideOpen.addEventListener('click', openSizeGuide);
    }
    e.sizeGuideClose.forEach(btn => {
      btn.addEventListener('click', closeSizeGuide);
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') closeSizeGuide();
    });

    // Add color button
    if (e.addColorBtn) {
      e.addColorBtn.addEventListener('click', addColorGroup);
    }

    // Type buttons
    GM_DEBUG.log('init: binding', e.typeBtns.length, 'type button listeners');
    e.typeBtns.forEach(btn => {
      btn.addEventListener('click', () => selectType(btn.dataset.type));
    });

    // Cart button
    if (e.cartBtn) {
      GM_DEBUG.log('init: binding cart button listener');
      e.cartBtn.addEventListener('click', submitToCart);
    } else {
      GM_DEBUG.warn('init: cart button (.gm-cob__cart-btn) not found');
    }

    // VAT toggle
    const vatCheckbox = e.root?.querySelector('.gm-cob__vat-checkbox');
    if (vatCheckbox) {
      vatCheckbox.addEventListener('change', () => {
        const isExVat = vatCheckbox.checked;
        if (e.vatText) e.vatText.textContent = isExVat ? (S.vatLabelEx || 'ex. VAT') : (S.vatLabelInc || 'inc. VAT');
        // Ensure state is initialized before updating display
        if (!state.current) resetCurrentGroup();
        renderPositions();
        updatePricingDisplay();
      });
    }

    updateAddColorButton();
    GM_DEBUG.log('init: calling refreshCartTotal and loadCustomizationVariants');
    refreshCartTotal().then(() => updatePricingDisplay());
    loadCustomizationVariants();
    GM_DEBUG.log('init: initialization complete');

    // Listen for cart updates to refresh the cached cart total
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        refreshCartTotal().then(() => updatePricingDisplay());
      });
    }

    // Debug: Log scroll and resize events
    if (DEBUG.enabled) {
      let scrollTimeout;
      window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (e.cartArea) {
            const rect = e.cartArea.getBoundingClientRect();
            const vh = window.innerHeight;
            DEBUG.log(`Scroll: cartArea bottom=${rect.bottom}px, vh=${vh}px, fixed=${rect.bottom > vh}`);
          }
        }, 100);
      });

      window.addEventListener('resize', () => {
        if (e.cartArea) {
          const rect = e.cartArea.getBoundingClientRect();
          DEBUG.log(`Resize: w=${window.innerWidth}px, h=${window.innerHeight}px`);
        }
      });

      DEBUG.log('Debug mode enabled - monitoring scroll/resize');
    }
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    GM_DEBUG.log('DOM still loading, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', init);
  } else {
    GM_DEBUG.log('DOM already ready, calling init immediately. readyState:', document.readyState);
    init();
  }
})();
